import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Container,
  Flex,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";

import ConfiguredSensorAlarmCard from "../components/ConfiguredSensorAlarmCard";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import { AlarmOccurrence, FieldTrigger, SensorDataTrigger } from "../types/alarmInterface";
import { IDevice } from "../types/deviceInterface";
import { IField } from "../types/fieldInterface";

type AlarmGroup = { latest: AlarmOccurrence; count: number };

const groupAlarms = (alarms: AlarmOccurrence[]) => {
  return Object.values(
    alarms.reduce<Record<string, AlarmGroup>>((groups, alarm) => {
      const groupKey = `${alarm.source_kind}:${alarm.source_key}:${alarm.device || ""}:${alarm.field || ""}`;
      if (!groups[groupKey]) {
        groups[groupKey] = { latest: alarm, count: 0 };
      }
      groups[groupKey].count += 1;
      if (new Date(alarm.created_at) > new Date(groups[groupKey].latest.created_at)) {
        groups[groupKey].latest = alarm;
      }
      return groups;
    }, {}),
  ).sort((left, right) => +new Date(right.latest.created_at) - +new Date(left.latest.created_at));
};

const AlarmsNewOverview = () => {
  const { fieldId } = useParams<{ fieldId: string }>();
  const api = useAxios();
  const toast = useToast();
  const [alarms, setAlarms] = useState<AlarmOccurrence[]>([]);
  const [sensorTriggers, setSensorTriggers] = useState<SensorDataTrigger[]>([]);
  const [fieldTriggers, setFieldTriggers] = useState<FieldTrigger[]>([]);
  const [fields, setFields] = useState<IField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState(fieldId || "");
  const [loading, setLoading] = useState(true);

  const loadAlarms = async () => {
    setLoading(true);
    try {
      const [
        alarmResponse,
        sensorTriggerResponse,
        fieldTriggerResponse,
        fieldResponse,
        deviceResponse,
      ] = await Promise.all([
        api.get<AlarmOccurrence[]>("/frontend/alarms/", {
          params: {
            ...(selectedFieldId ? { field: selectedFieldId } : {}),
          },
        }),
        api.get<SensorDataTrigger[]>("/frontend/alarm-triggers/sensor-data/"),
        api.get<FieldTrigger[]>("/frontend/alarm-triggers/field/"),
        api.get<IField[]>("/agtech/fields/"),
        selectedFieldId
          ? api.get<IDevice[]>(`/frontend/devices/by-field/${selectedFieldId}/`)
          : Promise.resolve({ data: [] as IDevice[] }),
      ]);
      const deviceIds = new Set(deviceResponse.data.map((device) => device.device_id));
      setAlarms(alarmResponse.data);
      setFields(fieldResponse.data);
      setSensorTriggers(
        selectedFieldId
          ? sensorTriggerResponse.data.filter((trigger) => deviceIds.has(trigger.device))
          : sensorTriggerResponse.data,
      );
      setFieldTriggers(
        selectedFieldId
          ? fieldTriggerResponse.data.filter((trigger) => trigger.field === Number(selectedFieldId))
          : fieldTriggerResponse.data,
      );
    } catch {
      toast({ title: <Trans>Unable to load alarms</Trans>, status: "error", isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlarms();
  }, [selectedFieldId]);

  const acknowledge = async (alarmId: number) => {
    try {
      await api.patch(`/frontend/alarms/${alarmId}/`, { acknowledge: true });
      await loadAlarms();
    } catch {
      toast({
        title: <Trans>Unable to acknowledge alarm</Trans>,
        status: "error",
        isClosable: true,
      });
    }
  };

  const remove = async (alarmId: number) => {
    try {
      await api.delete(`/frontend/alarms/${alarmId}/`);
      await loadAlarms();
    } catch {
      toast({ title: <Trans>Unable to delete alarm</Trans>, status: "error", isClosable: true });
    }
  };

  const hide = async (alarmId: number) => {
    try {
      await api.patch(`/frontend/alarms/${alarmId}/`, { hide: true });
      await loadAlarms();
    } catch {
      toast({ title: <Trans>Unable to hide alarm</Trans>, status: "error", isClosable: true });
    }
  };

  const removeSensorTrigger = async (trigger: SensorDataTrigger) => {
    try {
      await api.delete(`/frontend/alarm-triggers/sensor-data/${trigger.id}/`);
      setSensorTriggers((triggers) => triggers.filter(({ id }) => id !== trigger.id));
    } catch {
      toast({ title: <Trans>Unable to delete alarm</Trans>, status: "error", isClosable: true });
    }
  };

  const removeFieldTrigger = async (trigger: FieldTrigger) => {
    try {
      await api.delete(`/frontend/alarm-triggers/field/${trigger.id}/`);
      setFieldTriggers((triggers) => triggers.filter(({ id }) => id !== trigger.id));
    } catch {
      toast({ title: <Trans>Unable to delete alarm</Trans>, status: "error", isClosable: true });
    }
  };

  const activeGroups = groupAlarms(
    alarms.filter((alarm) => alarm.is_active && !alarm.acknowledged_at),
  );
  const acknowledgedGroups = groupAlarms(
    alarms.filter((alarm) => alarm.is_active && alarm.acknowledged_at),
  );
  const resolvedGroups = groupAlarms(alarms.filter((alarm) => alarm.resolved_at));

  const renderAlarmSection = (
    title: React.ReactNode,
    sectionGroups: AlarmGroup[],
    status: "active" | "acknowledged" | "resolved",
  ) => (
    <Box>
      <Heading size="md" mb={3}>
        {title}
      </Heading>
      <VStack align="stretch" spacing={3}>
        {sectionGroups.map(({ latest, count }) => (
          <Box
            key={`${status}:${latest.source_kind}:${latest.source_key}:${latest.id}`}
            borderWidth="1px"
            borderColor={status === "active" ? "red.300" : "gray.200"}
            bg={status === "active" ? "red.50" : "gray.50"}
            p={4}
            borderRadius={6}
          >
            <Flex justify="space-between" gap={4} wrap="wrap">
              <Stack spacing={1} maxW="800px">
                <HStack>
                  <Badge colorScheme={latest.severity === "ERROR" ? "red" : "yellow"}>
                    {latest.severity}
                  </Badge>
                  <Badge colorScheme={status === "active" ? "red" : "gray"}>
                    {status === "active" ? (
                      <Trans>Active</Trans>
                    ) : status === "acknowledged" ? (
                      <Trans>Acknowledged</Trans>
                    ) : (
                      <Trans>Resolved</Trans>
                    )}
                  </Badge>
                  {count > 1 && (
                    <Badge>
                      {count} <Trans>occurrences</Trans>
                    </Badge>
                  )}
                </HStack>
                <Text fontWeight="semibold">{latest.text}</Text>
                <Text fontSize="sm" color="gray.600">
                  {latest.device_name ||
                    latest.field_name ||
                    latest.source_kind.replaceAll("_", " ")}{" "}
                  · {new Date(latest.created_at).toLocaleString()}
                </Text>
              </Stack>
              <ButtonGroup size="sm" alignSelf="start">
                {status === "active" && (
                  <Button onClick={() => acknowledge(latest.id)}>
                    <Trans>Acknowledge</Trans>
                  </Button>
                )}
                {status === "acknowledged" && (
                  <Button variant="outline" onClick={() => hide(latest.id)}>
                    <Trans>Hide</Trans>
                  </Button>
                )}
                {status === "resolved" && (
                  <Button variant="outline" colorScheme="red" onClick={() => remove(latest.id)}>
                    <Trans>Delete</Trans>
                  </Button>
                )}
              </ButtonGroup>
            </Flex>
          </Box>
        ))}
        {!sectionGroups.length && (
          <Text color="gray.500" py={2}>
            <Trans>No alarms in this section.</Trans>
          </Text>
        )}
      </VStack>
    </Box>
  );

  return (
    <Navbar>
      <Container maxW="container.xl" py={7}>
        <Flex justify="space-between" gap={4} wrap="wrap" mb={6} align="end">
          <Box>
            <Heading size="lg">
              <Trans>Alarms</Trans>
            </Heading>
            <Text color="gray.500">
              {selectedFieldId ? (
                <Trans>Alarms for the selected field</Trans>
              ) : (
                <Trans>Current and resolved alarm occurrences</Trans>
              )}
            </Text>
          </Box>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={3}
            w={{ base: "full", md: "auto" }}
          >
            <Select
              value={selectedFieldId}
              onChange={(event) => setSelectedFieldId(event.target.value)}
              w={{ base: "full", md: "180px" }}
            >
              <option value="">
                <Trans>All fields</Trans>
              </option>
              {fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.field_name}
                </option>
              ))}
            </Select>
            <HStack w={{ base: "full", md: "auto" }}>
              <Tooltip
                label={<Trans>Creates an alarm trigger for a measurement on one device.</Trans>}
              >
                <Button
                  as={Link}
                  to="/alarms/add"
                  colorScheme="black"
                  variant="outline"
                  borderRadius={0}
                  flex={1}
                >
                  <Trans>Add Device Alarm</Trans>
                </Button>
              </Tooltip>
              <Tooltip
                label={
                  selectedFieldId ? (
                    <Trans>
                      Creates an alarm trigger for the average measurement across this field.
                    </Trans>
                  ) : (
                    <Trans>Select a field before creating a field alarm.</Trans>
                  )
                }
              >
                <Box flex={1}>
                  <Button
                    as={Link}
                    to={selectedFieldId ? `/alarms/alarm-field-add/${selectedFieldId}` : "/alarms"}
                    colorScheme="black"
                    variant="outline"
                    borderRadius={0}
                    w="full"
                    isDisabled={!selectedFieldId}
                  >
                    <Trans>Add Field Alarm</Trans>
                  </Button>
                </Box>
              </Tooltip>
            </HStack>
          </Stack>
        </Flex>

        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner />
          </Flex>
        ) : (
          <VStack align="stretch" spacing={6}>
            {renderAlarmSection(<Trans>Active alarms</Trans>, activeGroups, "active")}
            {renderAlarmSection(
              <Trans>Acknowledged alarms</Trans>,
              acknowledgedGroups,
              "acknowledged",
            )}
            {renderAlarmSection(<Trans>Resolved alarms</Trans>, resolvedGroups, "resolved")}

            <Box pt={8}>
              <Flex justify="space-between" align="center" mb={3} gap={4} wrap="wrap">
                <Box>
                  <Heading size="md">
                    <Trans>Configured Alarm Triggers</Trans>
                  </Heading>
                  <Text color="gray.500" fontSize="sm">
                    <Trans>Configured alarm triggers that are currently enabled or disabled.</Trans>
                  </Text>
                </Box>
              </Flex>
              <VStack align="stretch" spacing={3}>
                {sensorTriggers.map((trigger) => (
                  <ConfiguredSensorAlarmCard
                    key={`sensor-${trigger.id}`}
                    trigger={trigger}
                    onDelete={removeSensorTrigger}
                  />
                ))}
                {fieldTriggers.map((trigger) => (
                  <Box
                    key={`field-${trigger.id}`}
                    borderWidth="1px"
                    borderRadius={6}
                    p={4}
                    bgColor={"white"}
                  >
                    <Flex justify="space-between" gap={4} wrap="wrap">
                      <Stack spacing={1}>
                        <HStack>
                          <Badge colorScheme={trigger.is_enabled ? "green" : "gray"}>
                            {trigger.is_enabled ? <Trans>Enabled</Trans> : <Trans>Disabled</Trans>}
                          </Badge>
                          <Badge colorScheme={"gray"}>
                            <Trans>Field</Trans>
                          </Badge>
                          <Badge colorScheme={"gray"}>{trigger.severity}</Badge>
                        </HStack>
                        <Text fontWeight="semibold">{trigger.field_name}</Text>
                        <Text>
                          {trigger.metric.replaceAll("_", " ")}{" "}
                          {trigger.trigger_type === "AVERAGE" ? "average" : "single device"}{" "}
                          {trigger.comparison_direction.toLowerCase()} {trigger.trigger_value}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {trigger.notification_channels.join(", ")}
                        </Text>
                      </Stack>
                      <ButtonGroup
                        size="sm"
                        alignSelf="start"
                        flexDirection={{ base: "column", md: "row" }}
                        w={{ base: "full", md: "auto" }}
                      >
                        <Button
                          as={Link}
                          to={`/alarms/alarm-field-edit/${trigger.field}?trigger=${trigger.id}`}
                          w={{ base: "full", md: "auto" }}
                        >
                          <Trans>Edit</Trans>
                        </Button>
                        <Button
                          colorScheme="red"
                          variant="outline"
                          onClick={() => removeFieldTrigger(trigger)}
                          w={{ base: "full", md: "auto" }}
                        >
                          <Trans>Delete</Trans>
                        </Button>
                      </ButtonGroup>
                    </Flex>
                  </Box>
                ))}
                {!sensorTriggers.length && !fieldTriggers.length && (
                  <Text color="gray.500">
                    <Trans>No alarm rules have been configured.</Trans>
                  </Text>
                )}
              </VStack>

              <Box pt={6}>
                <Heading size="sm" mb={1}>
                  <Trans>System alarms</Trans>
                </Heading>
                <Text color="gray.500" fontSize="sm" mb={3}>
                  <Trans>
                    These persistent alarms are managed automatically and cannot be configured here.
                  </Trans>
                </Text>
                <VStack align="stretch" spacing={2}>
                  <Box borderWidth="1px" borderRadius={6} p={3} bg="gray.50">
                    <Text fontWeight="semibold">
                      <Trans>Low battery</Trans>
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <Trans>
                        Creates a warning when a device battery voltage is below 2.30 V. In-app and
                        email notifications are sent.
                      </Trans>
                    </Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius={6} p={3} bg="gray.50">
                    <Text fontWeight="semibold">
                      <Trans>Device offline</Trans>
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <Trans>
                        Creates a warning when a device has not reported data for more than 12 hours
                        and an alarm is configured for this device. In-app and email notifications
                        are sent.
                      </Trans>
                    </Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius={6} p={3} bg="gray.50">
                    <Text fontWeight="semibold">
                      <Trans>Valve flow anomaly</Trans>
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      <Trans>
                        Creates an error when an open valve has too little flow or a closed valve
                        has too much flow. In-app notifications are sent.
                      </Trans>
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="gray.500">
                    <Trans>Field status alarms are planned but are not active yet.</Trans>
                  </Text>
                </VStack>
              </Box>
            </Box>
          </VStack>
        )}
      </Container>
    </Navbar>
  );
};

export default AlarmsNewOverview;
